/**
 * The suppression list (admin doc 26 §3.3): addresses no message may go to.
 *
 * A hard bounce, a spam report, an SMS/WhatsApp STOP or an operator's entry puts an address
 * here, and the dispatcher records `suppressed_address` instead of sending. The console names a
 * PERSON and a channel, never an address: it is shown masked and never typed in, so no raw
 * number or mailbox lands in a browser cache or a screenshot on a ticket.
 *
 * Adding only ever stops messages, so it needs a note. Lifting re-exposes an address that
 * bounced or complained, so it needs a reason, and the audit log keeps the row as it was.
 */
import { useState } from 'react';
import { Button, Chip, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { useConsole } from '../app/console';
import {
  useAddSuppression,
  useRemoveSuppression,
  useSuppressions,
  type SuppressionIn,
  type SuppressionRow,
} from '../lib/api/hooks/notifications';

const PAGE_SIZE = 25;
const CHANNELS = ['sms', 'whatsapp', 'email', 'push'] as const;

/** What put the address here, in the words an operator would use on a call. */
export function sourceLabel(source: string): string {
  switch (source) {
    case 'hard_bounce':
      return 'bounced';
    case 'spam_report':
      return 'reported spam';
    case 'stop':
      return 'sent STOP';
    default:
      return 'added by an operator';
  }
}

export function SuppressionList() {
  const { toast } = useConsole();
  const [channel, setChannel] = useState('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [userId, setUserId] = useState('');
  const [newChannel, setNewChannel] = useState<SuppressionIn['channel']>('sms');
  const [lifting, setLifting] = useState<SuppressionRow | null>(null);

  const list = useSuppressions(channel, page, PAGE_SIZE);
  const add = useAddSuppression();
  const remove = useRemoveSuppression();

  const rows = list.data?.items ?? [];
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE_SIZE));
  const person = Number(userId);

  return (
    <Section
      title="Suppression list"
      note="Bounces, spam reports and STOPs land here by themselves; nothing is sent to these addresses"
      pad={false}
    >
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'end', padding: 'var(--sp-md)' }}>
        <Select
          label="Channel"
          value={channel}
          onChange={(next) => {
            setPage(1);
            setChannel(next);
          }}
          options={['', ...CHANNELS]}
          placeholder="Every channel"
        />
        <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
          Suppress a person
        </Button>
      </div>

      {list.isPending && <LoadingState message="Reading the suppression list…" />}
      {list.isError && (
        <ErrorState title="Could not read the suppression list" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="check"
          title="Nothing is suppressed"
          body="Every address we hold can be reached. That is the normal state."
          inline
        />
      )}
      {rows.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'channel', label: 'Channel' },
            { key: 'address', label: 'Address', mono: true },
            { key: 'source', label: 'Why' },
            { key: 'person', label: 'Person' },
            { key: 'note', label: 'Note' },
            { key: 'act', label: '' },
          ]}
          rows={rows.map((row) => ({
            id: row.id,
            channel: <Chip variant="filter" label={row.channel} />,
            address: row.destination_masked,
            source: (
              <StatusChip
                tone={row.source === 'manual' ? 'neutral' : 'warn'}
                label={sourceLabel(row.source)}
              />
            ),
            person: row.user_account_id ?? '—',
            note: row.note ?? '—',
            act: (
              <Button variant="secondary" size="sm" onClick={() => setLifting(row)}>
                Lift
              </Button>
            ),
          }))}
        />
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}

      <ConfirmAction
        open={adding}
        title="Suppress a person on one channel"
        phrase="SUPPRESS"
        reason="What they asked for, or why"
        confirmLabel="Suppress"
        blocked={Number.isInteger(person) && person > 0 ? undefined : ['Enter the person’s user id.']}
        consequences={[
          'Nothing more is sent to their current address on this channel — booking messages included.',
          'Their booking messages still reach them on their other channels.',
          'It stays until somebody lifts it, with a reason.',
        ]}
        onCancel={() => setAdding(false)}
        onConfirm={(note) => {
          add.mutate(
            { user_account_id: person, channel: newChannel, note },
            {
              onSuccess: () => {
                setAdding(false);
                setUserId('');
                toast({ message: 'Suppressed. Nothing more goes to that address.' });
              },
              // The server knows whether they have an address there, or are already listed.
              onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
            },
          );
        }}
      >
        <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
          <Input
            label="User id"
            value={userId}
            onChange={setUserId}
            inputMode="numeric"
            helper="From their profile. The address is looked up, never typed here."
          />
          <Select
            label="Channel"
            value={newChannel}
            onChange={(next) => setNewChannel(next as SuppressionIn['channel'])}
            options={[...CHANNELS]}
          />
        </div>
      </ConfirmAction>

      {lifting !== null && (
        <ConfirmAction
          open
          title={`Lift the suppression on ${lifting.destination_masked}`}
          phrase="LIFT"
          tone="error"
          reason="Why this address may be messaged again"
          confirmLabel="Lift it"
          consequences={[
            `This address ${sourceLabel(lifting.source)}. Messaging it again can cost the sender reputation.`,
            'The entry is removed. The audit log keeps it, with who lifted it and why.',
          ]}
          onCancel={() => setLifting(null)}
          onConfirm={(reason) => {
            remove.mutate(
              { id: lifting.id, reason },
              {
                onSuccess: () => {
                  setLifting(null);
                  toast({ message: 'Lifted.' });
                },
                onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
              },
            );
          }}
        />
      )}
    </Section>
  );
}
